import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { usePVData } from '../context/PVDataContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSun, FiTrendingUp, FiTree, FiCloud, FiCalendar, FiZap, FiDroplet, FiWind } = FiIcons;

const EnvironmentalImpact = () => {
  const { plants, getMonthlyData, getYearlyData } = usePVData();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // CO2-Einsparung Faktoren (Durchschnittswerte)
  const CO2_PER_KWH = 0.401; // kg CO2 pro kWh (deutscher Strommix)
  const TREES_PER_TON_CO2 = 45; // Anzahl Bäume die ca. 1 Tonne CO2 pro Jahr binden
  const WATER_PER_KWH = 1.5; // Liter Wasser gespart pro kWh Solarstrom (im Vergleich zu konventioneller Stromerzeugung)
  const KM_PER_KWH = 6.2; // Kilometer eines durchschnittlichen Elektroautos pro kWh

  // Berechne Gesamtstatistiken
  const totalProduction = plants.reduce((sum, plant) => sum + getYearlyData(plant.id, selectedYear), 0);
  
  // Berechne alle bisherigen Ertragsdaten
  const allYearsProduction = years.reduce((sum, year) => {
    const yearlyProduction = plants.reduce((yearSum, plant) => yearSum + getYearlyData(plant.id, year), 0);
    return sum + yearlyProduction;
  }, 0);

  // Umweltauswirkungen für ausgewähltes Jahr
  const co2Saved = (totalProduction * CO2_PER_KWH) / 1000; // Tonnen CO2
  const treeEquivalent = Math.round(co2Saved * TREES_PER_TON_CO2);
  const homesEquivalent = Math.round(totalProduction / 3500); // Durchschnittlicher Jahresverbrauch eines 4-Personen-Haushalts
  const waterSaved = Math.round(totalProduction * WATER_PER_KWH); // Liter Wasser
  const electricCarKm = Math.round(totalProduction * KM_PER_KWH); // Kilometer mit E-Auto

  // Umweltauswirkungen für alle Jahre
  const totalCo2Saved = (allYearsProduction * CO2_PER_KWH) / 1000; // Tonnen CO2
  const totalTreeEquivalent = Math.round(totalCo2Saved * TREES_PER_TON_CO2);
  const totalHomesEquivalent = Math.round(allYearsProduction / 3500);
  const totalWaterSaved = Math.round(allYearsProduction * WATER_PER_KWH); // Liter Wasser
  const totalElectricCarKm = Math.round(allYearsProduction * KM_PER_KWH); // Kilometer mit E-Auto

  // Jährliche CO2-Einsparung für Chart
  const yearlyChartData = years.map(year => {
    const yearlyProduction = plants.reduce((sum, plant) => sum + getYearlyData(plant.id, year), 0);
    const yearCo2Saved = (yearlyProduction * CO2_PER_KWH) / 1000;
    return {
      year,
      co2: parseFloat(yearCo2Saved.toFixed(2)),
      trees: Math.round(yearCo2Saved * TREES_PER_TON_CO2)
    };
  }).reverse(); // Umkehren, damit die Jahre aufsteigend angezeigt werden

  // Monatliche CO2-Einsparung für Chart
  const monthlyChartData = Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const monthName = new Date(2024, i).toLocaleDateString('de-DE', { month: 'short' });
    const totalMonthly = plants.reduce((sum, plant) => {
      return sum + getMonthlyData(plant.id, month, selectedYear);
    }, 0);
    const monthlyCo2 = (totalMonthly * CO2_PER_KWH) / 1000;
    return {
      month: monthName,
      co2: parseFloat(monthlyCo2.toFixed(2)),
      monthNum: month
    };
  });

  // Anlagen-Performance Daten für CO2-Einsparung
  const plantPerformanceData = plants.map(plant => {
    const yearlyProd = getYearlyData(plant.id, selectedYear);
    return {
      name: plant.name,
      co2: parseFloat(((yearlyProd * CO2_PER_KWH) / 1000).toFixed(2)),
      capacity: parseFloat(plant.capacity || 0)
    };
  });

  const COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#f97316'];

  // Dashboard-Statistiken mit Umweltauswirkungen
  const environmentalStats = [
    {
      title: "CO₂-Einsparung Gesamt",
      value: `${totalCo2Saved.toFixed(1)} t`,
      icon: FiCloud,
      color: "bg-blue-100 text-blue-600",
      description: "Seit Inbetriebnahme"
    },
    {
      title: "Bäume gepflanzt",
      value: totalTreeEquivalent.toLocaleString(),
      icon: FiTree,
      color: "bg-green-100 text-green-600",
      description: "Äquivalent"
    },
    {
      title: "Wasser gespart",
      value: `${(totalWaterSaved / 1000).toFixed(1)} m³`,
      icon: FiDroplet,
      color: "bg-cyan-100 text-cyan-600",
      description: "Verglichen mit konv. Energie"
    },
    {
      title: "E-Auto Reichweite",
      value: `${(totalElectricCarKm / 1000).toFixed(1)} Tkm`,
      icon: FiZap,
      color: "bg-purple-100 text-purple-600",
      description: "Mit erzeugtem Strom"
    },
  ];

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col md:flex-row md:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Umweltauswirkungen</h1>
          <p className="text-gray-600 mt-1">CO₂-Einsparung und Umweltbeitrag</p>
        </div>
        <div className="flex items-center space-x-4">
          <SafeIcon icon={FiCalendar} className="text-gray-500" />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Hauptstatistiken */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiTree} className="text-green-600 mr-2" />
            Umweltauswirkung {selectedYear}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">CO₂-Einsparung</p>
              <p className="text-2xl font-bold text-gray-900">{co2Saved.toFixed(2)} t</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">Baumäquivalent</p>
              <p className="text-2xl font-bold text-gray-900">{treeEquivalent}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">Haushalte versorgt</p>
              <p className="text-2xl font-bold text-gray-900">{homesEquivalent}</p>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-semibold text-gray-800 mb-3">Monatliche CO₂-Einsparung {selectedYear}</h3>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} t`, 'CO₂-Einsparung']} />
                  <Legend />
                  <Bar 
                    dataKey="co2" 
                    fill="#10b981" 
                    name="CO₂-Einsparung (t)" 
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiCloud} className="text-blue-600 mr-2" />
            Gesamte Umweltauswirkung
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">Gesamt CO₂-Einsparung</p>
              <p className="text-2xl font-bold text-gray-900">{totalCo2Saved.toFixed(2)} t</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">Gesamt Baumäquivalent</p>
              <p className="text-2xl font-bold text-gray-900">{totalTreeEquivalent}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">Gesamt Haushalte</p>
              <p className="text-2xl font-bold text-gray-900">{totalHomesEquivalent}</p>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="font-semibold text-gray-800 mb-3">Jährliche CO₂-Einsparung</h3>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={yearlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} t`, 'CO₂-Einsparung']} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="co2" 
                    stroke="#3b82f6" 
                    strokeWidth={3} 
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }} 
                    name="CO₂-Einsparung (t)" 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Visualisierungen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">CO₂-Einsparung nach Anlagen {selectedYear}</h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={plantPerformanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => [`${value} t`, 'CO₂-Einsparung']} />
                <Legend />
                <Bar 
                  dataKey="co2" 
                  fill="#10b981" 
                  name="CO₂-Einsparung (t)" 
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Verteilung der CO₂-Einsparung</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={plantPerformanceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="co2"
                  >
                    {plantPerformanceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} t`, 'CO₂-Einsparung']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3 overflow-y-auto max-h-[300px]">
              <h4 className="font-medium text-gray-900">Anlagen-Details</h4>
              {plantPerformanceData.map((plant, index) => (
                <div key={plant.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-4 h-4 rounded-full" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm md:text-base">{plant.name}</p>
                      <p className="text-xs md:text-sm text-gray-600">{plant.capacity} kWp</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900 text-sm md:text-base">{plant.co2} t CO₂</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Umweltwissen */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl shadow-sm p-6 border border-green-100"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <SafeIcon icon={FiSun} className="text-solar-500 mr-2" />
          Wussten Sie schon?
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium text-gray-900 mb-2">CO₂-Einsparung</h4>
            <p className="text-gray-700 text-sm">Jede kWh Solarstrom spart im Vergleich zum deutschen Strommix etwa 401 g CO₂ ein. Ihre Anlagen haben bisher {totalCo2Saved.toFixed(2)} Tonnen CO₂ eingespart.</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium text-gray-900 mb-2">Baumäquivalent</h4>
            <p className="text-gray-700 text-sm">Ein durchschnittlicher Baum bindet etwa 22 kg CO₂ pro Jahr. Ihre PV-Anlagen haben einen Effekt vergleichbar mit {totalTreeEquivalent} Bäumen.</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <h4 className="font-medium text-gray-900 mb-2">Versorgung</h4>
            <p className="text-gray-700 text-sm">Ein durchschnittlicher 4-Personen-Haushalt verbraucht etwa 3.500 kWh Strom pro Jahr. Ihre Anlagen könnten theoretisch {totalHomesEquivalent} Haushalte versorgen.</p>
          </div>
        </div>
      </motion.div>
      
      {/* Dashboard-Statistiken */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Umwelt-Dashboard</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {environmentalStats.map((stat, index) => (
            <motion.div 
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-sm text-gray-600">{stat.title}</h4>
                  <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className={`rounded-full p-3 ${stat.color}`}>
                  <SafeIcon icon={stat.icon} className="text-lg" />
                </div>
              </div>
              <p className="text-xs text-gray-500">{stat.description}</p>
            </motion.div>
          ))}
        </div>
        
        {/* Zusätzliche Umweltstatistiken */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100"
          >
            <div className="flex items-start space-x-4">
              <div className="rounded-full p-3 bg-green-100">
                <SafeIcon icon={FiWind} className="text-green-600 text-xl" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Vermiedene Emissionen</h4>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <p className="text-sm text-gray-600">Schwefeldioxid</p>
                    <p className="text-lg font-bold text-gray-900">{(totalCo2Saved * 1.5).toFixed(1)} kg</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Stickoxide</p>
                    <p className="text-lg font-bold text-gray-900">{(totalCo2Saved * 0.8).toFixed(1)} kg</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100"
          >
            <div className="flex items-start space-x-4">
              <div className="rounded-full p-3 bg-blue-100">
                <SafeIcon icon={FiZap} className="text-blue-600 text-xl" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Energieeinsparung</h4>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <p className="text-sm text-gray-600">Kohle gespart</p>
                    <p className="text-lg font-bold text-gray-900">{(allYearsProduction * 0.33 / 1000).toFixed(1)} t</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Erdgas gespart</p>
                    <p className="text-lg font-bold text-gray-900">{(allYearsProduction * 0.2).toFixed(0)} m³</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {plants.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-6 md:p-12 border border-gray-100 text-center"
        >
          <SafeIcon icon={FiSun} className="text-gray-300 text-4xl md:text-6xl mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Anlagen vorhanden</h3>
          <p className="text-gray-600 mb-6">Fügen Sie Ihre erste PV-Anlage hinzu, um Ihre Umweltauswirkungen zu sehen.</p>
          <button
            onClick={() => window.location.hash = '#/plants'}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
          >
            Erste Anlage hinzufügen
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default EnvironmentalImpact;